import React, { useRef } from 'react';
import { useHeroGlow, usePhoneCarousel } from './hooks';
import { useStoreModal } from './storeModalContext';

const SCREEN_COUNT = 3;

const Hero: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const { open: openStore } = useStoreModal();
  const activeScreen = usePhoneCarousel(phoneRef, SCREEN_COUNT);

  useHeroGlow(heroRef);

  const onStoreClick = () => openStore();

  const onTileClick = () => {
    const cta = heroRef.current?.querySelector<HTMLElement>('.hero-ctas .btn-primary-light');
    cta?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const screenClass = (i: number) => `phone-s${i === activeScreen ? ' active' : ''}`;

  return (
    <section id="hero" ref={heroRef}>
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-pattern" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice">
          <defs>
            <pattern id="khatam" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <g transform="translate(40 40)" fill="none" stroke="currentColor" strokeWidth="0.7" strokeLinejoin="miter">
                <rect x="-22" y="-22" width="44" height="44" />
                <rect x="-22" y="-22" width="44" height="44" transform="rotate(45)" />
                <circle cx="0" cy="0" r="3" fill="currentColor" stroke="none" opacity="0.6" />
              </g>
            </pattern>
          </defs>
          <rect width="800" height="800" fill="url(#khatam)" />
        </svg>
      </div>
      <div className="hero-inner">
        <div className="hero-copy">
          <span className="hero-eyebrow">The app to live Islam, not just learn it</span>
          <h1 className="hero-h1">
            Live Islam practically.<em>in &lt;5 mins a day.</em>
          </h1>
          <p className="hero-sub">
            Daily lessons built with scholars on the questions Muslims actually have - built for the day you actually live.
          </p>
          <div className="hero-ctas">
            <button type="button" className="btn-primary-light" onClick={onStoreClick}>
              Start your 7 day free trial
            </button>
          </div>
          <div className="hero-trust-strip">
            <span>
              <span className="hero-trust-stars">★</span>
              <strong>4.8</strong> on the App Store
            </span>
            <span className="hero-trust-divider" />
            <span>
              <strong>200K+</strong> Muslims
            </span>
            <span className="hero-trust-divider" />
            <span>
              Built with <strong>scholars and experts</strong>
            </span>
          </div>
        </div>

        <div className="hero-phone-wrap">
          <div className="phone-frame" ref={phoneRef} role="img" aria-label="Niyyah app preview">
            <div className="phone-notch" />
            <div className="phone-screen-wrap">
              {/* Screen 1: Topic Grid Home */}
              <div className={screenClass(0)}>
                <div className="ps-bar">
                  <span className="ps-time">9:41</span>
                  <span className="ps-icons">●●● 100%</span>
                </div>
                <div className="ps-hdr">
                  <div>
                    <div className="ps-greet">Assalamu Alaykum</div>
                    <div className="ps-name">Abdullah</div>
                  </div>
                  <div className="ps-streak">🔥 23</div>
                </div>
                <div className="ps-verse">
                  <div className="ps-verse-top">
                    <span className="ps-ve">Today&apos;s Verse</span>
                    <span className="ps-vr">2:286</span>
                  </div>
                  <div className="ps-va">لَا يُكَلِّفُ ٱللَّهُ نَفْسًا إِلَّا وُسْعَهَا</div>
                  <div className="ps-vt">&ldquo;Allah does not burden a soul beyond what it can bear&rdquo;</div>
                </div>
                <div className="ps-ctapill">
                  <span>Start today&apos;s lesson · 5 min</span>
                  <span className="arr">›</span>
                </div>
                <div className="ps-grid">
                  <button type="button" className="ps-tile c" onClick={onTileClick}>
                    <div className="ps-tile-icon">❤️</div>
                    <div className="ps-tile-name">Marriage &amp; relationships</div>
                  </button>
                  <button type="button" className="ps-tile o" onClick={onTileClick}>
                    <div className="ps-tile-icon">🏠</div>
                    <div className="ps-tile-name">Parenting &amp; family</div>
                  </button>
                  <button type="button" className="ps-tile t" onClick={onTileClick}>
                    <div className="ps-tile-icon">💰</div>
                    <div className="ps-tile-name">Money &amp; finance</div>
                  </button>
                  <button type="button" className="ps-tile p" onClick={onTileClick}>
                    <div className="ps-tile-icon">🌿</div>
                    <div className="ps-tile-name">Anxiety &amp; peace</div>
                  </button>
                </div>
              </div>

              {/* Screen 2: Lesson Card */}
              <div className={screenClass(1)}>
                <div className="ps-bar">
                  <span className="ps-time">9:41</span>
                  <span className="ps-icons">●●● 100%</span>
                </div>
                <div className="ps-progress">
                  <span className="on" />
                  <span className="on" />
                  <span className="on" />
                  <span className="on" />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <div className="ps-lesson-meta">
                  <span>Anxiety &amp; Peace</span>
                  <span>
                    <strong>Step 4</strong> of 8
                  </span>
                </div>
                <div className="ps-take">
                  <div className="ps-take-eyebrow">Scholar&apos;s Take</div>
                  <div className="ps-sheikh">
                    <div className="ps-sheikh-avatar">H</div>
                    <div>
                      <div className="ps-sheikh-name">Sheikh Hussan Mahmood</div>
                      <div className="ps-sheikh-role">Reviewed this lesson</div>
                    </div>
                    <span
                      className="badge-verified"
                      style={{ marginLeft: 'auto', fontSize: 8, padding: '3px 6px' }}
                    >
                      Verified
                    </span>
                  </div>
                  <p className="ps-take-body">
                    Tawakkul is not surrender of effort. It is doing your part - the call, the application, the conversation -
                    <em>and then</em> trusting Allah with the outcome. Practise it today by naming one thing you can act on, and
                    one thing you must release.
                  </p>
                </div>
              </div>

              {/* Screen 3: Prayer Tab */}
              <div className={screenClass(2)}>
                <div className="ps-bar">
                  <span className="ps-time">9:41</span>
                  <span className="ps-icons">●●● 100%</span>
                </div>
                <div className="ps-prayer-hdr">
                  <div className="ps-prayer-eyebrow">Prayer · Milton Keynes</div>
                  <div className="ps-prayer-title">Today</div>
                </div>
                <div className="ps-next">
                  <div>
                    <div className="ps-next-label">Next prayer</div>
                    <div className="ps-next-name">Asr</div>
                  </div>
                  <div className="ps-next-time">3:48 PM</div>
                </div>
                <div className="ps-prayer-list">
                  <div className="ps-prayer-row done">
                    <div className="dot">✓</div>
                    <div className="name">Fajr</div>
                    <div className="time">5:42 AM</div>
                  </div>
                  <div className="ps-prayer-row done">
                    <div className="dot">✓</div>
                    <div className="name">Dhuhr</div>
                    <div className="time">12:58 PM</div>
                  </div>
                  <div className="ps-prayer-row now">
                    <div className="dot" />
                    <div className="name">Asr</div>
                    <div className="time">3:48 PM</div>
                  </div>
                  <div className="ps-prayer-row">
                    <div className="dot" />
                    <div className="name">Maghrib</div>
                    <div className="time">6:23 PM</div>
                  </div>
                  <div className="ps-prayer-row">
                    <div className="dot" />
                    <div className="name">Isha</div>
                    <div className="time">7:51 PM</div>
                  </div>
                </div>
                <div className="ps-prayer-bottom">
                  <div className="ps-streak-ring">
                    <div className="ps-ring">
                      <span>6/7</span>
                    </div>
                    <div>
                      <div className="ps-ring-label">This week</div>
                      <div className="ps-ring-meta">6 of 7</div>
                    </div>
                  </div>
                  <div className="ps-qibla">
                    <div className="ps-compass" />
                    <div>
                      <div className="ps-qibla-label">Qibla</div>
                      <div className="ps-qibla-deg">118°</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="phone-dots" aria-hidden="true">
              {Array.from({ length: SCREEN_COUNT }).map((_, i) => (
                <div key={i} className={`phone-dot${i === activeScreen ? ' on' : ''}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
